import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, ArrowRight, Star, Zap, Shield, Users, TrendingUp, Crown, Mail, Flag } from 'lucide-react';
import { mockData } from '../../mock/data';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

const PricingPreview = () => {
  const { pricing } = mockData;
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('residential');

  // Main proxy locations with flag CDN
  const proxyLocations = [
    { country: 'United States', flagCode: 'us', cities: 50 },
    { country: 'United Kingdom', flagCode: 'gb', cities: 8 },
    { country: 'Germany', flagCode: 'de', cities: 12 },
    { country: 'France', flagCode: 'fr', cities: 10 },
    { country: 'Canada', flagCode: 'ca', cities: 15 },
    { country: 'Australia', flagCode: 'au', cities: 8 },
    { country: 'Japan', flagCode: 'jp', cities: 6 },
    { country: 'Brazil', flagCode: 'br', cities: 12 }
  ];

  const residentialFeatures = [
    t('pricingPreview.features.unlimitedSessions'),
    t('pricingPreview.features.freeGeoTargeting'),
    t('pricingPreview.features.support247'),
    t('pricingPreview.features.noHiddenFees'),
    t('pricingPreview.features.ipQualityEngine'),
    t('pricingPreview.features.responseTime')
  ];

  return (
    <section className="py-24 bg-slate-900" id="pricing-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
            {t('pricingPage.simpleTransparent') || 'Simple, Transparent'} <span className="text-cyan-400">{t('pricingPage.pricing') || 'Pricing'}</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            {t('pricingPage.generalSubtitle') || 'Choose the perfect plan for your needs. No hidden fees, no setup costs.'}
          </p>
        </div>

        {/* Pricing Tabs */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl overflow-hidden">
            <div className="flex bg-slate-800/50 p-3">
              <button
                onClick={() => setActiveTab('residential')}
                className={`flex-1 px-8 py-5 rounded-xl font-semibold text-lg md:text-xl transition-all duration-200 ${
                  activeTab === 'residential'
                    ? 'bg-cyan-500 text-slate-950 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {t('pricingPreview.residentialProxies')}
              </button>
              <button
                onClick={() => setActiveTab('mobile')}
                className={`flex-1 px-8 py-5 rounded-xl font-semibold text-lg md:text-xl transition-all duration-200 ${
                  activeTab === 'mobile'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                {t('pricingPreview.unlimitedMobile')}
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-10">
            {activeTab === 'residential' && (
              <div className="text-center">
                
                {/* Pricing Tiers */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                    <h4 className="text-base font-semibold text-slate-400 mb-3">{t('pricingPreview.starter')}</h4>
                    <div className="text-2xl font-bold text-cyan-400 mb-2">$1.30</div>
                    <div className="text-sm text-slate-400">{t('pricingPreview.perGB')}</div>
                    <div className="text-sm text-slate-500 mt-3">{t('pricingPreview.volumeRange1')}</div>
                  </div>
                  
                  <div className="bg-slate-800/50 border border-cyan-500/50 rounded-xl p-5 ring-1 ring-cyan-500/25">
                    <h4 className="text-base font-semibold text-slate-400 mb-3">{t('pricingPreview.professional')}</h4>
                    <div className="text-2xl font-bold text-cyan-400 mb-2">$1.10</div>
                    <div className="text-sm text-slate-400">{t('pricingPreview.perGB')}</div>
                    <div className="text-sm text-slate-500 mt-3">{t('pricingPreview.volumeRange2')}</div>
                    {language === 'en' && (
                      <Badge className="bg-green-500/10 text-green-400 border-0 text-sm mt-3">
                        {t('pricingPreview.save')} 15%
                      </Badge>
                    )}
                  </div>
                  
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                    <h4 className="text-base font-semibold text-slate-400 mb-3">{t('pricingPreview.business')}</h4>
                    <div className="text-2xl font-bold text-cyan-400 mb-2">$1.00</div>
                    <div className="text-sm text-slate-400">{t('pricingPreview.perGB')}</div>
                    <div className="text-sm text-slate-500 mt-3">{t('pricingPreview.volumeRange3')}</div>
                    {language === 'en' && (
                      <Badge className="bg-green-500/10 text-green-400 border-0 text-sm mt-3">
                        {t('pricingPreview.save')} 23%
                      </Badge>
                    )}
                  </div>
                  
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                    <h4 className="text-base font-semibold text-slate-400 mb-3">{t('pricingPreview.scale')}</h4>
                    <div className="text-2xl font-bold text-cyan-400 mb-2">$0.90</div>
                    <div className="text-sm text-slate-400">{t('pricingPreview.perGB')}</div>
                    <div className="text-sm text-slate-500 mt-3">{t('pricingPreview.volumeRange4')}</div>
                    {language === 'en' && (
                      <Badge className="bg-green-500/10 text-green-400 border-0 text-sm mt-3">
                        {t('pricingPreview.save')} 31%
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Key Features */}
                <div className="bg-slate-800/30 rounded-xl p-6 mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {residentialFeatures.map((feature, index) => (
                      <div key={index} className="flex items-center text-slate-300">
                        <Check className="w-4 h-4 text-cyan-400 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Link to="/pricing/residential">
                  <Button className="h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-10 py-4 text-lg">
                    {t('pricingPreview.viewAllPlans')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {activeTab === 'mobile' && (
              <div className="text-center">
                
                <div className="space-y-4 mb-8">
                  {pricing.mobile.plans.map((plan, index) => (
                    <div 
                      key={index}
                      className={`bg-slate-800/50 border rounded-xl p-5 ${
                        index === 0 
                          ? 'border-green-500/50 ring-1 ring-green-500/25' 
                          : 'border-slate-700'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <h4 className="text-lg font-bold text-white">{t(`pricingPreview.mobilePlans.${plan.name.toLowerCase()}.name`)}</h4>
                            {index === 0 && (
                              <span className="bg-green-500 text-white px-2 py-1 rounded text-sm font-medium">
                                {t('pricingPreview.trial').toUpperCase()}
                              </span>
                            )}
                          </div>
                          <p className="text-base text-slate-400">{t(`pricingPreview.mobilePlans.${plan.name.toLowerCase()}.volume`)}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-green-400">{plan.price === 'Custom' ? t('pricingPreview.custom') : plan.price}</div>
                          <div className="text-sm text-slate-400">{t(`pricingPreview.mobilePlans.${plan.name.toLowerCase()}.unit`)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Link to="/pricing/mobile">
                  <Button className="h-11 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-10 py-4 text-lg">
                    {t('pricingPreview.viewAllMobilePlans')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Global Proxy Locations */}
        <div className="text-center mb-16">
          <div className="max-w-4xl mx-auto mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">{t('pricingPreview.countriesAvailable')}</h3>
            <p className="text-lg text-slate-400 mb-12">{t('pricingPreview.accessProxiesWorldwide')}</p>
          </div>
          
          {/* Scrolling Country Flags */}
          <div className="relative overflow-hidden mb-12">
            <style dangerouslySetInnerHTML={{
              __html: `
                @keyframes scroll-left {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                .scroll-animation {
                  animation: scroll-left 30s linear infinite;
                }
              `
            }} />
            
            {/* Left fade overlay */}
            <div className="absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none"></div>
            
            {/* Right fade overlay */}
            <div className="absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none"></div>
            
            <div className="scroll-animation flex items-center gap-16 whitespace-nowrap">
              {/* First set of countries */}
              {proxyLocations.map((location, index) => (
                <div key={index} className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                  <div className="w-12 h-8 md:w-20 md:h-12 rounded-lg overflow-hidden shadow-lg border border-slate-600">
                    <img 
                      src={`https://flagcdn.com/w160/${location.flagCode}.png`}
                      alt={`${location.country} flag`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-left">
                    <h4 className="text-white font-semibold text-base md:text-xl">{t(`pricingPreview.countries.${location.country}`)}</h4>
                    <p className="text-slate-400 text-xs md:text-base">{location.cities} {t('pricingPreview.cities')}</p>
                  </div>
                </div>
              ))}
              {/* Duplicate set for seamless scrolling */}
              {proxyLocations.map((location, index) => (
                <div key={`duplicate-${index}`} className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                  <div className="w-12 h-8 md:w-20 md:h-12 rounded-lg overflow-hidden shadow-lg border border-slate-600">
                    <img 
                      src={`https://flagcdn.com/w160/${location.flagCode}.png`}
                      alt={`${location.country} flag`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-left">
                    <h4 className="text-white font-semibold text-base md:text-xl">{t(`pricingPreview.countries.${location.country}`)}</h4>
                    <p className="text-slate-400 text-xs md:text-base">{location.cities} {t('pricingPreview.cities')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <Link to="/pricing">
              <Button className="h-11 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-10 py-4 text-lg">
{t('pricingPreview.viewAllLocations')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

export default PricingPreview;